import React, { useEffect,  useState, useReducer, useMemo } from 'react';

import { Row, Col, Select, Typography, Space, DatePicker, TimePicker,  message, Spin, Popover, Avatar, Card, Button, Drawer, Form, Checkbox, Radio, Empty } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { FundViewOutlined, InfoOutlined, FilterOutlined } from '@ant-design/icons';
// import PropTypes from 'prop-types';
import { useAuth } from '../../Context';
import vesseltrackerreducer from './reducer';
import { AIS_DATA_LIST_ERROR, AIS_DATA_LIST_SUCCESS, AIS_NAME_FILTER, AIS_NAME_FILTER_ERROR, AIS_NAME_FILTER_SUCCESS,  DETECTION_ALGORITHM_LIST, DETECTION_ALGORITHM_LIST_ERROR, DETECTION_ALGORITHM_LIST_SUCCESS, DETECTION_CLUSTER_LIST, DETECTION_CLUSTER_LIST_ERROR, DETECTION_CLUSTER_LIST_SUCCESS, DETECTION_CLUSTER_VESSEL_DATA_LIST, DETECTION_CLUSTER_VESSEL_DATA_LIST_ERROR, DETECTION_CLUSTER_VESSEL_DATA_LIST_SUCCESS, DETECTION_GROUP_LIST, DETECTION_GROUP_LIST_ERROR, DETECTION_GROUP_LIST_SUCCESS, } from './constants';
import axios from 'axios';
import dayjs from 'dayjs';
import SeaMap from './SeaMap';
import GoogleMap from '../../Map/map';
import Chart from 'react-apexcharts';
import useResizeObserver from 'use-resize-observer';

import englan from '../../../english.json';
import germanlan from '../../../german.json';
import arabiclan from '../../../arabic.json';
import frenchlan from '../../../french.json';
import hindilan from '../../../hindi.json';
import malayalamlan from '../../../malayalam.json';
import { datetimeCovertertolocal } from '../../datetime-converter';


const { Title } = Typography;


const { RangePicker } = DatePicker;

let realtimedetection_data_pagination = {};

// ============================================================
// Pure helpers for the fiberDistance-vs-timeStart chart.
// Defined outside the component since they don't touch any
// component state/props.
// ============================================================

// Linearly resample an array to a target length (shrink or grow it),
// walking evenly through the original array's index positions and
// interpolating between neighbouring values where needed.
const resampleArray = (arr, targetLength) => {
	if (!Array.isArray(arr) || arr.length === 0 || targetLength <= 0) return [];
	if (arr.length === targetLength) return arr;
	if (arr.length === 1) return new Array(targetLength).fill(arr[0]);
	if (targetLength === 1) return [arr[0]];

	const result = [];
	const lastIndex = arr.length - 1;
	for (let i = 0; i < targetLength; i++) {
		const pos = (i / (targetLength - 1)) * lastIndex;
		const lower = Math.floor(pos);
		const upper = Math.ceil(pos);
		const weight = pos - lower;

		if (upper >= arr.length) {
			result.push(arr[lastIndex]);
		} else if (lower === upper) {
			result.push(arr[lower]);
		} else {
			result.push(arr[lower] + (arr[upper] - arr[lower]) * weight);
		}
	}
	return result;
};

// Coerce a timeStart value (ISO string or epoch ms) to epoch ms.
const toEpochMs = (value) => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return value; // already epoch ms
	// numeric strings (e.g. "1728712345000") are treated as epoch ms
	if (typeof value === 'string' && value.trim() !== '' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
		return Number(value.trim());
	}
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.valueOf() : null;
};

// Stable color palette used to color each line by vessel.
const ALGORITHM_COLOR_PALETTE = ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A', '#26a69a', '#D10CE8'];

// ============================================================
// Coordinate helpers for the map trajectories.
// detectioncollections groups several detections, so its arrays can be
// nested, stored as strings, and can contain placeholder values
// (0 / null / out-of-range). These make sure only real coordinates ever
// reach SeaMap, so auto-centering fits the actual detections.
// ============================================================
const flattenDeep = (v) => {
	if (Array.isArray(v)) return v.flat(Infinity);
	if (v === null || v === undefined || v === '') return [];

	// Tolerate coordinates stored as a JSON string ("[1.2,3.4]") or a
	// comma-separated string ("1.2,3.4").
	if (typeof v === 'string') {
		const trimmed = v.trim();
		if (trimmed.startsWith('[')) {
			try {
				const parsed = JSON.parse(trimmed);
				return Array.isArray(parsed) ? parsed.flat(Infinity) : [parsed];
			} catch (e) {
				return [];
			}
		}
		if (trimmed.includes(',')) {
			return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
		}
	}

	return [v];
};

const isValidCoord = (lat, lng) =>
	Number.isFinite(lat) &&
	Number.isFinite(lng) &&
	Math.abs(lat) <= 90 &&
	Math.abs(lng) <= 180 &&
	!(lat === 0 && lng === 0);

// Builds [{ lat, lng, index }] from parallel lat / lng arrays. `index` is the
// ORIGINAL array position so parallel arrays (distance / speed / time) stay aligned.
const buildCoordPoints = (latArr, lngArr) => {
	const lats = flattenDeep(latArr);
	const lngs = flattenDeep(lngArr);

	return lats
		.map((lat, index) => ({
			lat: parseFloat(lat),
			lng: parseFloat(lngs[index]),
			index,
		}))
		.filter((point) => isValidCoord(point.lat, point.lng));
};

// ============================================================
// PERFORMANCE: chart downsampling.
//
// ApexCharts creates SVG nodes for every point/marker, so a series with
// tens of thousands of points freezes the Graph tab. Each series is
// reduced to at most MAX_CHART_POINTS_PER_SERIES points using LTTB
// (Largest-Triangle-Three-Buckets), which keeps the visual shape of the
// line (peaks / dips) while dropping redundant points. Series that are
// already small are returned untouched.
// ============================================================
const MAX_CHART_POINTS_PER_SERIES = 1500;

const downsamplePoints = (points, threshold) => {
	const total = Array.isArray(points) ? points.length : 0;
	if (total === 0 || threshold >= total || threshold < 3) return points;

	const sampled = [points[0]];
	const bucketSize = (total - 2) / (threshold - 2);
	let anchorIndex = 0;

	for (let i = 0; i < threshold - 2; i++) {
		// Average of the NEXT bucket (the "look-ahead" point)
		const nextStart = Math.floor((i + 1) * bucketSize) + 1;
		const nextEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, total);
		let avgX = 0;
		let avgY = 0;
		const nextLength = Math.max(nextEnd - nextStart, 1);
		for (let j = nextStart; j < nextEnd; j++) {
			avgX += points[j].x;
			avgY += points[j].y;
		}
		avgX /= nextLength;
		avgY /= nextLength;

		// Pick the point in the CURRENT bucket forming the largest triangle
		const rangeStart = Math.floor(i * bucketSize) + 1;
		const rangeEnd = Math.floor((i + 1) * bucketSize) + 1;
		const anchor = points[anchorIndex];
		let maxArea = -1;
		let chosen = rangeStart;

		for (let j = rangeStart; j < rangeEnd; j++) {
			const area = Math.abs(
				(anchor.x - avgX) * (points[j].y - anchor.y) -
				(anchor.x - points[j].x) * (avgY - anchor.y)
			);
			if (area > maxArea) {
				maxArea = area;
				chosen = j;
			}
		}

		sampled.push(points[chosen]);
		anchorIndex = chosen;
	}

	sampled.push(points[total - 1]);
	return sampled;
};

export default function ShipTracker() {

	const { state } = useLocation();

	const dashboardtype = process.env.REACT_APP_IDENTIFIER;
	const authtoken = localStorage.getItem(`${dashboardtype.toUpperCase()}_ACCESS_TOKEN`);
	const {getRegion, regiondata, getUserAllocation ,getUserAllocationData,getPids,pidsData,channelData, getChannels, handleGetMapdata, mapdata, optionalpipedata, handleOptionalPipelinedata} = useAuth();
	const initalState = {realtimedetection_data:[],users_data:[], pending: false, error: false,detectionalgorithm:[],aisdata:[],vessel_name:[],ais_name:[]};
	const [realtimedetection_data, dispatch] = useReducer(vesseltrackerreducer, initalState);

	const customstart = localStorage.getItem(`${dashboardtype.toUpperCase()}_START_TIME`);
	const customend = localStorage.getItem(`${dashboardtype.toUpperCase()}_END_TIME`);

	// ---- List / Map / Graph toggle (mirrors RealtimeDetection.jsx's List/Map
	// pattern and AlarmReport.jsx's List/Graph pattern). Defaults to 'Map' so
	// the page behaves exactly as before unless the user switches tabs. ----
	const [view, setView] = useState('Map');
	// const record = state && state.data ? state.data : null;
	const record = state?.data ?? null;

	useEffect(() => {
		if (record) {
			setFilterButton(true);
			dispatch({
				type: DETECTION_GROUP_LIST_SUCCESS,
				payload: record
			});
		}
	}, []);
	

	// Used to size the Graph view's chart responsively, same as AlarmReport.jsx.
	const { ref, width } = useResizeObserver();


	// ---- Date/Time helpers (mirror AlarmReport.jsx's getTimeValue / EndTimeValue) ----
	const getTimeValue = (customTime) => {
		const time = customTime ? customTime : '00:00:00';
		const parts = time.split(':').map(Number);
		const [h = 0, m = 0, s = 0] = parts;
		return dayjs().set('hour', h).set('minute', m).set('second', s);
	};
	

	const getEndTimeValue = (customTime) => {
		const time = customTime ? customTime : '23:59:59';
		const parts = time.split(':').map(Number);
		const [h = 0, m = 0, s = 0] = parts;
		return dayjs().set('hour', h).set('minute', m).set('second', s);
	};

	// ---- Advanced options (mirrors AlarmReport.jsx / RealtimeAlarm module) ----
	const [timeSelection, setTimeSelection] = useState(
		state && (state.data.startdatetime || state.data.enddatetime) ? true : false
	);
	const [filterLastHour, setFiltetLastHour] = useState(1);
	const [advDateRange, setAdvDateRange] = useState([
		state && state.data.startdatetime ? dayjs(state.data.startdatetime) : dayjs().subtract(30, 'day'),
		state && state.data.enddatetime ? dayjs(state.data.enddatetime) : dayjs()
	]);
	const [startTime, setStartTime] = useState(getTimeValue(customstart));
	const [endTime, setEndTime] = useState(getEndTimeValue(customend));

	const navigate = useNavigate();
	// const [regionId, setRegionId] = useState(
	// 	state && state.data.regionId ? state && state.data.regionId : null
	// );
	const pipelineId = localStorage.getItem(`${dashboardtype.toUpperCase()}_pipelineId`) ? localStorage.getItem(`${dashboardtype.toUpperCase()}_pipelineId`) : null;
	const [pidsFilter, setpidsFilter] = useState(1);
	const [vesselFilter, setvesselFilter] = useState([]);
	const [aisNameFilter, setAisNameFilter] = useState([]);
	const [timeInterval, setTimeInterval] = useState(60);
	const [channelsdata, setchannels] = useState(null);
	const [clusterCheck, setclusterCheck] = useState(false);
	const [filterButton, setFilterButton] = useState(false);
	// const [detectionCollectionView, setdetectionCollectionView] = useState(record);
	const [TrackFilter, setTrackFilter] = useState('RANSAC-CV');
	const [cluster, setcluster] = useState(1);
	const [algorithm, setalgorithm] = useState(null);
	// const [trajectory, settrajectory] = useState(null);
	const permission = localStorage.getItem(`${dashboardtype.toUpperCase()}_PERMISSION`);
	const admin_roles = ['superadmin','admin','Station-In-Charge','Pipeline-HO','Pipeline-In-Charge','HQO-HSE'];
	// const [allocationId, setAllocationId] = useState(
	// 	state && state.data.regionId ? state && state.data.regionId : null
	// );
	const apiUrl = localStorage.getItem(`${dashboardtype.toUpperCase()}_API_URL`);
	const api = apiUrl && apiUrl ? apiUrl : process.env.REACT_APP_CORE_HOST;

	const envTimeout = process.env.REACT_APP_TIMEOUT == undefined || process.env.REACT_APP_TIMEOUT == 'undefined' ? 5000 : process.env.REACT_APP_TIMEOUT;
	const timeout = localStorage.getItem(`${dashboardtype.toUpperCase()}_TIMEOUT`) == 'null' || localStorage.getItem(`${dashboardtype.toUpperCase()}_TIMEOUT`) == 'undefined' ? parseInt(envTimeout) :  parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_TIMEOUT`));
	const envdateformat = process.env.REACT_APP_DATE_FORMAT == undefined || process.env.REACT_APP_DATE_FORMAT == 'undefined' ? 'YYYY-MM-DD' : process.env.REACT_APP_DATE_FORMAT;
	const settingsdateformat = localStorage.getItem(`${dashboardtype.toUpperCase()}_DATE_FORMAT`) == 'null' || localStorage.getItem(`${dashboardtype.toUpperCase()}_DATE_FORMAT`) == 'undefined' ? envdateformat : localStorage.getItem(`${dashboardtype.toUpperCase()}_DATE_FORMAT`);
	const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) && localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
	const langdata = langid && langid ==1 ? arabiclan : langid ==2 ? englan : langid ==3 ? frenchlan : langid ==4 ? germanlan : langid ==5 ? hindilan : langid == 6 ? malayalamlan : englan;
	const featuresModule = JSON.parse(localStorage.getItem(`${dashboardtype.toUpperCase()}_FEATURES_MODULE_DASHBOARD`));
	const map_type = localStorage.getItem(`${dashboardtype.toUpperCase()}_MAP_TYPE`);

	// ---- Side filter drawer ----
	const [filterDrawer, setFilterdrawer] = useState(false);

	// ---- Resolve the effective { start, end } Date range from the CURRENT draft
	// advanced-options state (timeSelection / advDateRange / startTime / endTime /
	// filterLastHour). Used only at mount (to seed the applied state) and at
	// "Apply" time (handleFilterDetection) — mirrors AlarmReport.jsx, where the
	// date range used for the request is only (re)computed when the filter is
	// actually applied, not on every keystroke/selection. ----
	const computeDateRange = () => {
		if (timeSelection) {
			const sDate = advDateRange && advDateRange[0] ? advDateRange[0] : dayjs();
			const eDate = advDateRange && advDateRange[1] ? advDateRange[1] : dayjs();
			const sTime = startTime || getTimeValue(customstart);
			const eTime = endTime || getEndTimeValue(customend);

			const combinedStart = dayjs(sDate)
				.set('hour', sTime.hour())
				.set('minute', sTime.minute())
				.set('second', sTime.second())
				.set('millisecond', 0);

			const combinedEnd = dayjs(eDate)
				.set('hour', eTime.hour())
				.set('minute', eTime.minute())
				.set('second', eTime.second())
				.set('millisecond', 999);

			return { start: combinedStart.toDate(), end: combinedEnd.toDate() };
		}

		const now = dayjs();
		return { start: now.subtract(filterLastHour, 'hour').toDate(), end: now.toDate() };
	};

	// ---- "Applied" filter state — this is what actually drives the API request.
	// It only changes when the user clicks "Apply" in the filter drawer
	// (handleFilterDetection), exactly like AlarmReport.jsx's filter values are
	// only committed to a fetch on its "Apply" button. ----
	const [appliedPidsFilter, setAppliedPidsFilter] = useState(pidsFilter);
	const [appliedvesselFilter, setAppliedvesselFilter] = useState(vesselFilter);
	const [appliedaisNameFilter, setAppliedaisNameFilter] = useState(aisNameFilter);
	const [appliedChannelsdata, setAppliedChannelsdata] = useState(channelsdata);
	const [appliedTrackFilter, setAppliedTrackFilter] = useState(TrackFilter);
	const [appliedAlgorithm, setAppliedAlgorithm] = useState(algorithm);
	const [appliedTimeInterval, setAppliedTimeInterval] = useState(timeInterval);
	// const [appliedTrajectory, setAppliedTrajectory] = useState(trajectory);
	const [appliedStartDate, setAppliedStartDate] = useState(() => computeDateRange().start);
	const [appliedEndDate, setAppliedEndDate] = useState(() => computeDateRange().end);

	let pidsfilter=[];
	let clusterfilter = [
		{ value: 1, label: `${langdata && langdata.Normal ? langdata.Normal : 'Normal'}` },
		{ value: 2, label: `${langdata && langdata.Algorithm ? langdata.Algorithm : 'Algorithm'}` },];
	let tracktypefilter = [
		{ value: 'VITERBI-BEAM', label: 'VITERBI-BEAM' },
		{ value: 'RANSAC-CV', label: 'RANSAC-CV' },
		{ value: 'SEED-GROW', label: 'SEED-GROW' },
		{ value: 'SEED-GROW-KINEMATIC' , label: 'SEED-GROW-KINEMATIC' },];

	pidsData && pidsData.map((pid)=>{
		pidsfilter.push({
			value:pid.id,
			label:pid.name.toUpperCase()});
	});

	let channels=[];
	let algorithamOptions =[];

	// let trajectoryOptions =[
		
	// 	{
	// 		value: null,
	// 		label: `${langdata && langdata.Normal ? langdata.Normal:'Normal'}`,
	// 	},
	// 	{
	// 		value: true,
	// 		label: `${langdata && langdata.Corrected ? langdata.Corrected:'Corrected'}`,
	// 	},
	// ];

	const lastHours = [
		{ value: 1, label: `1 ${langdata && langdata.Hour ? langdata.Hour : 'Hour'}` },
		{ value: 2, label: `2 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
		{ value: 3, label: `3 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
		{ value: 6, label: `6 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
		{ value: 12, label: `12 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
		{ value: 24, label: `24 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
	];
	const timeIntervalOptions = [
		{ value: 15, label: `15 ${langdata && langdata.Minutes ? langdata.Minutes : 'Minutes'}` },
		{ value: 30, label: `30 ${langdata && langdata.Minutes ? langdata.Minutes : 'Minutes'}` },
		{ value: 45, label: `45 ${langdata && langdata.Minutes ? langdata.Minutes : 'Minutes'}` },
		{ value: 60, label: `60 ${langdata && langdata.Minutes ? langdata.Minutes : 'Minutes'}` },
		// { value: 12, label: `12 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
		// { value: 24, label: `24 ${langdata && langdata.Hours ? langdata.Hours : 'Hours'}` },
	];

	realtimedetection_data.detectionalgorithm && realtimedetection_data.detectionalgorithm.map((item) => {
		
		algorithamOptions.push({
			value : item.id,
			label : item?.name ? item.name : '',
		});
	});


	let vesselOptions =[{value: null , label : langdata && langdata.ALL ? langdata.ALL :  'ALL'}];
	let aisNameOptions =[{value: null , label : langdata && langdata.ALL ? langdata.ALL :  'ALL'}];
	realtimedetection_data.vessel_name && realtimedetection_data.vessel_name.map((item) => {
		
		vesselOptions.push({
			value : item.id ? item.id : null,
			label : item?.vesselName ? item.vesselName : '',
		});
	});
	realtimedetection_data.ais_name && realtimedetection_data.ais_name.map((item) => {
		
		aisNameOptions.push({
			value : item.name ? item.name : null,
			label : item?.name ? item.name : '',
		});
	});

	channelData && channelData.map((item) => {
		channels.push({
			value : item.channelId,
			label : item.name,
		});
	});

	const disabledDate = (current) => {
		return current && current > dayjs().endOf('day');
	};

	// ---- Advanced-options handlers (draft only — do not trigger a fetch) ----
	const changeTimeSelection = (e) => {
		setTimeSelection(e.target.checked);
	};

	const handleLastHour = (value) => {
		setvesselFilter(null);
		setFiltetLastHour(value);
	};

	const handleAdvDateRange = (dates) => {
		if (dates) {
			setAdvDateRange([dates[0], dates[1]]);
		}
	};

	const handleStartTime = (time) => {
		if (time) {
			setStartTime(time);
		}
	};

	const handleEndTime = (time) => {
		if (time) {
			setEndTime(time);
		}
	};

	useEffect(() => {
		if(featuresModule.realtimeDetectionEnabled == false) {
			navigate('/dashboard');
		}
		window.scrollTo(0, 0);
		localStorage.setItem(dashboardtype.toUpperCase()+ '_ACTIVE_MENU_ITEM' , 1139);
	}, []);

	// ---- Region-driven lookups + map data (mirrors AlarmReport.jsx's first
	// useEffect — depends only on authtoken/region/allocation, not on any of
	// the drawer's filter values). ----
	useEffect(()=>{
		if(!authtoken){
			navigate('/');
		}
		else{
			if(admin_roles.includes(permission)){
				getPids(authtoken);
				getRegion(authtoken);
			}
			else{
				getUserAllocation(authtoken);
			}
			loadDetectionAlgorithm();
			// loadAsnData();
			// getDetectionClusterVesselData();
			if (map_type && map_type === 'leaflet') {
				handleGetMapdata(authtoken,  null, null);
				handleOptionalPipelinedata(authtoken, pipelineId,  null, null);
			}
		}
	},[authtoken,]);

	// ---- Cascading dropdown data (channels) — these follow the DRAFT pids
	// selection live, purely to populate the drawer's own option list,
	// independent of whether "Apply" has been clicked. ----
	useEffect(() => {
		if(authtoken){
			if(pidsFilter!=null){
				getChannels(authtoken, pidsFilter);
				loadAisData();
			}
		}
	},[authtoken, pidsFilter, channelsdata]);

	// ---- Actual data fetch. Mirrors AlarmReport.jsx's getAlarm/getMapAlarm
	// useEffect: only fires on region/allocation change or when the APPLIED
	// filter state changes — i.e. only after "Apply" is clicked in the filter
	// drawer, never on a raw filter selection. ----
	useEffect(() => {
		if (authtoken && appliedAlgorithm !== null && record == null) {
			if(cluster === 1){

				getDetectionClusterData(1);
			}
			else{
				getDetectionGroupData(1);
			}
			loadAisData();
		}
	}, [
		authtoken,
		appliedvesselFilter,
		appliedPidsFilter,
		appliedChannelsdata,
		appliedAlgorithm,
		appliedStartDate,
		appliedEndDate,
		appliedaisNameFilter,
		appliedTrackFilter
	]);
	

	let regions=[{value:null,
		label:`${langdata && langdata.ALL ? langdata.ALL:'ALL'}`
	}];

	if(admin_roles.includes(permission)){
		regiondata && regiondata.map((region)=>{
			regions.push({
				value:region.id,
				label:region.regionName.toUpperCase()});
		});
	} else {
		getUserAllocationData && getUserAllocationData.map((region)=>{
			regions.push({
				value:region.id,
				label: region.regioninfo.regionName+'(' + region.pipelineConfigFrom+' - '+region.pipelineConfigTo + ')',
				region: region.regioninfoId,
				pipelineConfigFrom : region.pipelineConfigFrom,
				pipelineConfigTo : region.pipelineConfigTo
			});
		});
	}

	// ---- Pipeline / Fiber / Border / Optional-path geometry, grouped by region (mirrors GMap.jsx / AlarmReport.jsx) ----
	//
	// PERFORMANCE: this whole block is memoized on mapdata / optionalpipedata.
	// Previously it was rebuilt on every render of ShipTracker (every drawer
	// click, every state change), and because it produced brand-new arrays
	// each time, react-leaflet re-applied setLatLngs() on every pipeline /
	// fibre polyline on every render. Now the SAME array references are
	// handed to SeaMap until the underlying map data actually changes.
	const { pipelineTracks, fiberTracks, pipelineBorderTracks, optionalPathTracks } = useMemo(() => {
		const tempMapArray = {};
		const teampOptionalMapArray = {};

		mapdata && mapdata.map((item) => {
			const fieldValue = item['regioninfoId'];
			if (!tempMapArray[fieldValue]) {
				tempMapArray[fieldValue] = [];
			}
			tempMapArray[fieldValue].push(item);
		});

		optionalpipedata && optionalpipedata.map((item) => {
			const fieldValue = item['regioninfoId'];
			if (!teampOptionalMapArray[fieldValue]) {
				teampOptionalMapArray[fieldValue] = [];
			}
			teampOptionalMapArray[fieldValue].push(item);
		});

		const pipelineTracks = Object.values(tempMapArray).map((item) =>
			item.map((data) => [data.latitude, data.longitude])
		);

		const fiberTracks = Object.values(tempMapArray).map((item) =>
			item.map((data) => [data.fibreLatitude, data.fibreLongitude])
		);

		const pipelineBorderTracks = Object.values(tempMapArray).map((item) =>
			item.map((data) => [data.latitude, data.longitude])
		);

		const optionalPathTracks = Object.values(teampOptionalMapArray).map((item) =>
			item.map((data) => [data.walkingLatitude, data.walkingLongitude])
		);

		return { pipelineTracks, fiberTracks, pipelineBorderTracks, optionalPathTracks };
	}, [mapdata, optionalpipedata]);

	// ============================================================
	// ROU MARKERS — KMP / WB / TP (Turn) / TLP / CH
	// Ported 1:1 from RealtimeEvents.jsx — unrelated to the detection API,
	// left untouched.
	//
	// PERFORMANCE: memoized on mapdata so the marker arrays keep a stable
	// identity between renders (otherwise every MarkerClusterGroup was
	// rebuilt on each render).
	// ============================================================
	const { KMP, WB, Turn, TLP, CH } = useMemo(() => {
		const KMP = [];
		const WB = [];
		const Turn = [];
		const TLP = [];
		const CH = [];

		mapdata && mapdata.map((item) => {
			if (item.rouMarker == 'CH') {
				CH.push({
					geocode: [item.latitude, item.longitude],
					pop: `${langdata && langdata.CH ? langdata.CH : 'CH'}`,
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage !== null && item?.pipelineChainage !== undefined ? item.pipelineChainage : null,
					ODMeter: item?.ODMeter !== null && item?.ODMeter !== undefined ? item.ODMeter : null,
				});
			}
			else if (item.rouMarker == 'WB') {
				WB.push({
					geocode: [item.latitude, item.longitude],
					pop: `${langdata && langdata.WB ? langdata.WB : 'WB'}`,
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage,
					ODMeter: item?.ODMeter,
				});
			}
			else if (item.rouMarker == 'TLP') {
				TLP.push({
					geocode: [item.latitude, item.longitude],
					pop: `${langdata && langdata.TLP ? langdata.TLP : 'TLP'}`,
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage,
					ODMeter: item?.ODMeter,
				});
			}
			else if (item.rouMarker == 'KMP') {
				KMP.push({
					geocode: [item.latitude, item.longitude],
					pop: `${langdata && langdata.KMP ? langdata.KMP : 'KMP'}`,
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage != null ? item.pipelineChainage : null,
					ODMeter: item?.ODMeter !== null && item?.ODMeter !== undefined ? item.ODMeter : null,
				});
			}
			else if (item.rouMarker == 'TP') {
				Turn.push({
					geocode: [item.latitude, item.longitude],
					pop: `${langdata && langdata.TP ? langdata.TP : 'TP'}`,
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage,
					ODMeter: item?.ODMeter,
				});
			}
		});

		return { KMP, WB, Turn, TLP, CH };
	}, [mapdata]);

	// PERFORMANCE: memoized on the reducer's detection array. The reducer
	// only produces a new array when a fetch completes, so this expensive
	// per-record transformation (including a datetime conversion per record)
	// no longer re-runs on every unrelated render.
	const DetectionClusterData = useMemo(() => {
		const DetectionClusterData = [];

		realtimedetection_data.realtimedetection_data && realtimedetection_data.realtimedetection_data.map((item) => {
			const datetime = datetimeCovertertolocal(item.starttime ? item.starttime : null);
			const endtime = datetimeCovertertolocal(item.endtime ? item.endtime : null);
			DetectionClusterData.push({
				id: item?.id,
				channelName: item?.channelinfo?.name || item?.channelname ? item?.channelinfo?.name || item?.channelname : '',
				// eventPosition: item?.channelRange ? item.channelRange : [],
				// distanceRange: item?.distanceRange && item?.distanceRange !== null ? item.distanceRange : [],
				fiberLatitude: item?.fiberLatitude && item?.fiberLatitude !== null ? item.fiberLatitude : [],
				fiberLongitude: item?.fiberLongitude && item?.fiberLongitude !== null ? item.fiberLongitude : [],
				fiberDistance: item?.fiberDistance && item?.fiberDistance !== null ? item.fiberDistance : [],
				sourceDistances: item?.sourceDistances && item?.sourceDistances !== null ? item.sourceDistances : [],
				sourceLatitude: item?.sourceLatitude && item?.sourceLatitude !== null ? item.sourceLatitude : [],
				realTimeStart: item?.realTimeStart && item?.realTimeStart !== null ? item.realTimeStart : [],
				realTimeEnd: item?.realTimeEnd && item?.realTimeEnd !== null ? item.realTimeEnd : [],
				realSourceLatitude: item?.realSourceLatitude && item?.realSourceLatitude !== null ? item.realSourceLatitude : [],
				realSourceLongitude: item?.realSourceLongitude && item?.realSourceLongitude !== null ? item.realSourceLongitude : [],
				realSourceDistances: item?.realSourceDistances && item?.realSourceDistances !== null ? item.realSourceDistances : [],
				realSourceSpeedms: item?.realSourceSpeedms && item?.realSourceSpeedms !== null ? item.realSourceSpeedms : [],
				sourceLongitude: item?.sourceLongitude && item?.sourceLongitude !== null ? item.sourceLongitude : [],
				timeStart: item?.timeStart && item?.timeStart !== null ? item.timeStart : [],
				sourceSpeedms: item?.sourceSpeedms && item?.sourceSpeedms !== null ? item.sourceSpeedms : [],
				// peakCoordinates: item?.peakCoordinates && item?.peakCoordinates !== null ? item.peakCoordinates : 'No Data',
				detectionAlgorithmTypeName: item?.detectionalgorithmtype?.name || item?.detectionalgname ? item?.detectionalgname || item?.detectionalgorithmtype?.name : '',
				intervalMin: item?.intervalMin ? item.intervalMin : '',
				startFiberLength: item?.startFiberLength ? item.startFiberLength : '',
				endFiberLength: item?.endFiberLength ? item.endFiberLength : '',
				starttime: `${datetime.localdate} ${datetime.localtime}`,
				endtime: `${endtime.localdate} ${endtime.localtime}`,
				multipleModelName: item?.multiplemodels?.name ? item?.multiplemodels?.name : '',
				eventType: item?.eventType ? item.eventType : '',
				pidsinfoId: item?.pidsinfoId ? item.pidsinfoId : '',
				trackType: item?.trackType ? item.trackType : '',
				seedStartDistance: item?.seedStartDistance ? item.seedStartDistance : '',
				seedEndDistance: item?.seedEndDistance ? item.seedEndDistance : '',
				sourceTimeStart: item?.sourceTimeStart ? item.sourceTimeStart : '',
				sourceTimeEnd: item?.sourceTimeEnd ? item.sourceTimeEnd : '',
				seeddatetime: item?.seeddatetime ? item.seeddatetime : '',
				rawStartTime: item?.starttime ? item.starttime : '',
				rawEndTime: item?.endtime ? item.endtime : '',
				detectionalgorithmtypeId: item?.detectionalgorithmtypeId ? item.detectionalgorithmtypeId : '',
				vesselName: item?.vesselName ? item.vesselName : '',
				regioninfoId: item?.regioninfoId ? item.regioninfoId : '',
				channelId: item?.channelId ? item.channelId : '',
				multiplemodelsId: item?.multiplemodelsId ? item?.multiplemodelsId : '',
				
				// detectionResults: item?.detectionResults ? item.detectionResults : {},
				// correctedDetectionResults: item?.correctedDetectionResults ? item.correctedDetectionResults : {},
				// isTrajectoryProcessed: item?.isTrajectoryProcessed ? item.isTrajectoryProcessed : '',
				// isTrajectoryCorrected: item?.isTrajectoryCorrected ? item.isTrajectoryCorrected : '',
				createdAt: item?.createdAt ? item.createdAt : '',
				updatedAt: item?.updatedAt ? item.updatedAt : '',
				pipelineId: item?.pipelineId ? item.pipelineId : '',
				regionName: item?.regioninfo?.regionName ? item?.regioninfo?.regionName : '',
				// hidden: item?.hidden ?? false,
				pidsName: item?.pidsinfo?.name || item?.pidsname ? item?.pidsname || item?.pidsinfo?.name : '',
				// peakDistance: item?.peakDistance &&  item?.peakDistance !== null ? item.peakDistance.toFixed(2) : 'No Data',
			});
		});

		return DetectionClusterData;
	}, [realtimedetection_data.realtimedetection_data]);

	// realtimedetection_data.clustergroup_data && realtimedetection_data.clustergroup_data.map((item) => {
	// 	const datetime = datetimeCovertertolocal(item.starttime ? item.starttime : null);
	// 	const endtime = datetimeCovertertolocal(item.endtime ? item.endtime : null);
	// 	DetectionGroupData.push({
	// 		id: item?.id,
	// 		channelName: item?.channelinfo?.name ? item?.channelinfo?.name : '',
	// 		fiberLatitude: item?.fiberLatitude && item?.fiberLatitude !== null ? item.fiberLatitude : [],
	// 		fiberLongitude: item?.fiberLongitude && item?.fiberLongitude !== null ? item.fiberLongitude : [],
	// 		fiberDistance: item?.fiberDistance && item?.fiberDistance !== null ? item.fiberDistance : [],
	// 		sourceDistances: item?.sourceDistances && item?.sourceDistances !== null ? item.sourceDistances : [],
	// 		sourceLatitude: item?.sourceLatitude && item?.sourceLatitude !== null ? item.sourceLatitude : [],
	// 		sourceLongitude: item?.sourceLongitude && item?.sourceLongitude !== null ? item.sourceLongitude : [],
	// 		detectionAlgorithmTypeName: item?.detectionalgorithmtype?.name ? item?.detectionalgorithmtype?.name : '',
	// 		starttime: `${datetime.localdate} ${datetime.localtime}`,
	// 		endtime: `${endtime.localdate} ${endtime.localtime}`,
	// 		eventType: item?.eventType ? item.eventType : '',
	// 		trackType: item?.trackType ? item.trackType : '',
	// 		seedStartDistance: item?.seedStartDistance ? item.seedStartDistance : '',
	// 		seedEndDistance: item?.seedEndDistance ? item.seedEndDistance : '',
	// 		sourceTimeStart: item?.sourceTimeStart ? item.sourceTimeStart : '',
	// 		sourceTimeEnd: item?.sourceTimeEnd ? item.sourceTimeEnd : '',
	// 		seeddatetime: item?.seeddatetime ? item.seeddatetime : '',
	// 		pidsinfoId: item?.pidsinfoId ? item.pidsinfoId : '',
	// 		detectionalgorithmtypeId: item?.detectionalgorithmtypeId ? item.detectionalgorithmtypeId : '',
	// 		channelId: item?.channelId ? item.channelId : '',
	// 		createdAt: item?.createdAt ? item.createdAt : '',
	// 		updatedAt: item?.updatedAt ? item.updatedAt : '',
	// 		pipelineId: item?.pipelineId ? item.pipelineId : '',
	// 		pidsName: item?.pidsinfo?.name ? item?.pidsinfo?.name : '',
	// 	});
	// });

	let ASNdata = [];
	realtimedetection_data.aisdata && realtimedetection_data.aisdata.map((item) => {
		ASNdata.push({
			datetime: item?.datetime ? item.datetime : [],
			latitude: item?.latitude ? item.latitude : [],
			longitude: item?.longitude ? item.longitude : [],
			name: item?.name ? item.name : 'No Data',
			speed: item?.speed ? item.speed : [],
			
		});
	});

	// PERFORMANCE: keep the SAME ASNdata reference until the AIS response
	// itself changes, so SeaMap's memoized AIS layer is not rebuilt on every
	// ShipTracker render.
	const stableASNdata = useMemo(() => ASNdata, [realtimedetection_data.aisdata]);


	// ============================================================
	// Build the props SeaMap actually needs, straight from
	// DetectionClusterData.
	//
	//   - points: built from the parallel sourceLatitude / sourceLongitude
	//     arrays, IN ORDER. Index 0 stays "the start" — SeaMap already
	//     renders the first point of a trajectory with the vessel-CPA
	//     icon and every point after it as a plain circle marker, so we
	//     don't need to do anything special here, just preserve order.
	//   - realPoints: same idea for realSourceLatitude / realSourceLongitude
	//     (drawn by the "Realtime Points" / "Realtime Lines" overlays).
	//   - fibre markers: built the same way from fiberLatitude /
	//     fiberLongitude / fiberDistance (one fibre point per index).
	//   - startFiberLength / endFiberLength / starttime are already the
	//     final, display-ready values computed above (starttime has
	//     already been through datetimeCovertertolocal), so SeaMap just
	//     shows them as-is on hover — it must NOT reformat them again.
	//   - Coordinates go through buildCoordPoints, which tolerates nested /
	//     string-encoded arrays and drops placeholder / out-of-range
	//     values, so both /detectioncluster and /detectioncollections data
	//     centre the map on the real detections.
	//
	// PERFORMANCE: memoized on DetectionClusterData so SeaMap receives the
	// same trajectories array until new detections are fetched. This is what
	// lets SeaMap's memoized detection layer skip re-rendering.
	// ============================================================
	const detectionTrajectoriesForMap = useMemo(() => DetectionClusterData.map((item) => {
		const toArr = (v) => {
			if (Array.isArray(v)) return v;
			if (v === null || v === undefined || v === '') return [];
			return [v];
		};

		// Algorithm trajectory points (sourceLatitude / sourceLongitude)
		const points = buildCoordPoints(item.sourceLatitude, item.sourceLongitude);

		// Real-time points (realSourceLatitude / realSourceLongitude)
		const realPoints = buildCoordPoints(item.realSourceLatitude, item.realSourceLongitude);

		// Per-point start time, localized once here (SeaMap shows it as-is).
		// Falls back to the trajectory's own start time if realTimeStart is empty.
		const toLocalString = (v) => {
			if (v === null || v === undefined || v === '') return null;
			const d = datetimeCovertertolocal(v);
			return `${d.localdate} ${d.localtime}`;
		};
		const rawRealTimes = toArr(item.realTimeStart);
		const realStartTimeHover = rawRealTimes.length > 0
			? rawRealTimes.map(toLocalString)
			: item.starttime;

		return {
			id: item.id,
			detectionAlgorithmTypeId: item.detectionalgorithmtypeId,
			algorithmName: item.detectionAlgorithmTypeName,
			channelName: item.channelName,
			pidsName: item.pidsName,
			eventType: item.eventType,
			vesselName: item.vesselName,
			startFiberLength: item.startFiberLength,
			endFiberLength: item.endFiberLength,
			startTimeHover: item.starttime,
			trackType: item.trackType ? item.trackType : null,
			seedStartDistance: item.seedStartDistance,
			seedEndDistance: item.seedEndDistance,
			sourceTimeStart: item.sourceTimeStart,
			sourceTimeEnd: item.sourceTimeEnd,
			seeddatetime: item.seeddatetime,
			fiberLatitude: Array.isArray(item.fiberLatitude) ? item.fiberLatitude : [],
			sourceDistances: Array.isArray(item.sourceDistances) ? item.sourceDistances : [],
			sourceSpeedms: Array.isArray(item.sourceSpeedms) ? item.sourceSpeedms : [],
			fiberLongitude: Array.isArray(item.fiberLongitude) ? item.fiberLongitude : [],
			fiberDistance: Array.isArray(item.fiberDistance) ? item.fiberDistance : [],
			points,

			// ---- REAL-TIME data ----
			realPoints,
			realTotalPoints: flattenDeep(item.realSourceLatitude).length,
			realSourceDistances: toArr(item.realSourceDistances),
			realSourceSpeedms: toArr(item.realSourceSpeedms),
			realStartTimeHover,
		};
	}), [DetectionClusterData]);

	// ============================================================
	// fiberDistance (x) vs timeStart (y) chart data — one series
	// per detection record.
	//
	//   - If fiberDistance has exactly ONE value, a single point is
	//     plotted using that distance and the FIRST timeStart value.
	//     The remaining timeStart values are ignored (not resampled).
	//   - Otherwise timeStart is resampled (shrunk/grown) to match the
	//     shape of fiberDistance so the two arrays line up
	//     point-for-point before being plotted.
	//
	// Works for BOTH /detectioncluster and /detectioncollections (and for
	// the data passed through router `state`), because every source ends up
	// in DetectionClusterData. fiberDistance / timeStart are run through
	// flattenDeep because detectioncollections groups several detections,
	// so these arrays can be nested or string-encoded.
	//
	// Each series is colored by a group name:
	//   - vesselName   (detectioncluster)
	//   - trackType    (detectioncollections — it has no vesselName)
	// so multiple lines sharing the same name share a color, and the legend
	// tells them apart.
	//
	// PERFORMANCE: memoized on DetectionClusterData, and each series is
	// downsampled (LTTB) to MAX_CHART_POINTS_PER_SERIES points so the chart
	// stays responsive with very large detections.
	// ============================================================
	const detectionFiberTimeSeries = useMemo(() => DetectionClusterData
		.map((item) => {
			// Returns the first candidate that has real (non-empty) values,
			// flattened. Used so the graph works for detectioncluster,
			// detectioncollections AND the data passed through router state,
			// which may not carry every field.
			const firstNonEmpty = (...candidates) => {
				for (const candidate of candidates) {
					const flat = flattenDeep(candidate).filter(
						(v) => v !== null && v !== undefined && v !== ''
					);
					if (flat.length > 0) return flat;
				}
				return [];
			};

			// Distances: fiberDistance first (API data), then fall back to the
			// source / real-source distances (router state data).
			const distances = firstNonEmpty(
				item.fiberDistance,
				item.sourceDistances,
				item.realSourceDistances
			)
				.map((d) => parseFloat(d))
				.filter((d) => Number.isFinite(d));

			// Times: timeStart first (API data), then sourceTimeStart/End,
			// then realTimeStart, and finally the record's own start/end time.
			const timeSource = firstNonEmpty(
				item.timeStart,
				[item.sourceTimeStart, item.sourceTimeEnd],
				item.realTimeStart,
				[item.rawStartTime, item.rawEndTime]
			);

			const rawTimes = timeSource
				.map(toEpochMs)
				.filter((t) => t !== null);

			if (distances.length === 0 || rawTimes.length === 0) return null;

			let points;

			if (distances.length === 1) {
				// Single distance → one point: (distance[0], first timeStart).
				// Remaining timeStart values are intentionally not used.
				points = [{ x: rawTimes[0], y: distances[0] }];
			} else {
				// shrink/grow timeStart to match the shape of fiberDistance
				const times = resampleArray(rawTimes, distances.length);

				points = distances
					.map((d, index) => ({ x: times[index], y: d }))
					.sort((a, b) => a.x - b.x);
			}

			// vesselName for detectioncluster, trackType for detectioncollections.
			const groupName = item.vesselName
				? item.vesselName
				: (item.trackType ? item.trackType : 'Unknown Vessel');

			return {
				id: item.id,
				name: `${groupName}`,
				vesselName: groupName,
				algorithmTypeId: item.detectionalgorithmtypeId,
				algorithmTypeName: item.detectionAlgorithmTypeName || 'Unknown',
				data: downsamplePoints(points, MAX_CHART_POINTS_PER_SERIES),
			};
		})
		.filter(Boolean), [DetectionClusterData]);

	// Assign a stable color per group (records sharing a vessel name /
	// track type get the same color).
	const vesselColorMap = {};
	let vesselColorIndex = 0;

	detectionFiberTimeSeries.forEach((s) => {
		const key = s.vesselName || 'Unknown Vessel';

		if (!(key in vesselColorMap)) {
			vesselColorMap[key] =
				ALGORITHM_COLOR_PALETTE[vesselColorIndex % ALGORITHM_COLOR_PALETTE.length];

			vesselColorIndex += 1;
		}
	});

	const fiberTimeSeriesColors = detectionFiberTimeSeries.map(
		(s) => vesselColorMap[s.vesselName || 'Unknown Vessel']
	);

	// ---------- Chart sizing / axis config ----------
	const PX_PER_POINT = 12;                  // horizontal room per data point
	const MAX_CHART_WIDTH = 30000;            // browsers struggle with SVGs wider than ~32k px
	const X_LABEL_SPACING_PX = 90;            // gap between time labels (time only)
	const X_LABEL_SPACING_WITH_DATE_PX = 150; // gap between time labels (date + time)
	const X_AXIS_PADDING_PERCENT = 0.02;      // 2% of the time range on each side
	const X_AXIS_MIN_PADDING_MS = 30 * 1000;  // fallback when all points share one time
	const ONE_DAY_MS = 24 * 60 * 60 * 1000;

	// x values are TIMES (epoch ms), y values are distances.
	const allTimes = detectionFiberTimeSeries.flatMap((s) => s.data.map((p) => p.x));

	let xAxisMin;
	let xAxisMax;

	if (allTimes.length > 0) {
		// reduce instead of Math.min(...arr) to avoid "Maximum call stack size exceeded"
		const minTime = allTimes.reduce((acc, v) => (v < acc ? v : acc), Infinity);
		const maxTime = allTimes.reduce((acc, v) => (v > acc ? v : acc), -Infinity);
		const range = maxTime - minTime;
		const padding = Math.max(range * X_AXIS_PADDING_PERCENT, X_AXIS_MIN_PADDING_MS);

		xAxisMin = Math.floor(minTime - padding);
		xAxisMax = Math.ceil(maxTime + padding);
	}

	// If the data spans more than a day, labels show date + time so they stay unambiguous.
	const spansMultipleDays =
		xAxisMin !== undefined && xAxisMax !== undefined && (xAxisMax - xAxisMin) > ONE_DAY_MS;

	const maxPointsInSeries = detectionFiberTimeSeries.reduce(
		(acc, s) => (s.data.length > acc ? s.data.length : acc),
		0
	);

	// Visible width of the card, minus its padding. `width` is undefined on first render.
	const containerWidth = width ? Math.max(width - 40, 300) : 800;

	// Chart grows with the data; the wrapper below scrolls horizontally when it's wider than the card.
	const chartWidth = Math.min(
		MAX_CHART_WIDTH,
		Math.max(containerWidth, maxPointsInSeries * PX_PER_POINT)
	);

	// One label per ~90px (or ~150px with dates) -> never congested, however wide the chart is.
	const tickAmount = Math.max(
		2,
		Math.floor(chartWidth / (spansMultipleDays ? X_LABEL_SPACING_WITH_DATE_PX : X_LABEL_SPACING_PX))
	);

	// Format an epoch-ms / ISO value using the shared converter so the graph
	// follows the same time format as the rest of the app.
	const formatTime = (val, includeDate = false) => {
		if (!val) return '';
		const d = datetimeCovertertolocal(val);
		return includeDate ? `${d.localdate} ${d.localtime}` : d.localtime;
	};

	// Per-series marker size: single-point series stay visible as dots, long
	// series hide markers (a line is enough) and show one on hover.
	// Every plotted point gets a visible marker. Single-point series use a
	// slightly larger dot so they stand out.
	const markerSizes = detectionFiberTimeSeries.map((s) =>
		s.data.length <= 1 ? 5 : 4
	);

	const fiberTimeSeriesColorsKey = fiberTimeSeriesColors.join(',');
	const markerSizesKey = markerSizes.join(',');

	const fiberDistanceTimeOptions = useMemo(() => ({
		chart: {
			toolbar: { show: false },
			zoom: { enabled: true },
			animations: { enabled: false },
		},
		colors: fiberTimeSeriesColors,
		dataLabels: { enabled: false },
		stroke: { curve: 'straight', width: 2 },
		markers: { size: markerSizes, shape: 'circle', hover: { size: 6 } },
		title: {
			text: `${langdata && langdata.FibreDistancevsTime ? langdata.FibreDistancevsTime : 'Fibre Distance vs Time'}`,
			align: 'left'
		},
		legend: {
			show: true,
			position: 'top',
			// left-aligned so it stays visible at the start of the scroll area
			horizontalAlign: 'left',
			onItemClick: { toggleDataSeries: true },
			onItemHover: { highlightDataSeries: true },
		},
		yaxis: {
			type: 'numeric',
			title: { text: `${langdata && langdata.Distance ? langdata.Distance : 'Distance'} (m)` },
			labels: {
				formatter: (val) => (val !== undefined ? Number(val).toFixed(2) : val),
			},
		},
		xaxis: {
			type: 'datetime',
			min: xAxisMin,
			max: xAxisMax,
			tickAmount,
			title: { text: `${langdata && langdata.Time ? langdata.Time : 'Time'}` },
			labels: {
				rotate: 0,
				hideOverlappingLabels: true,
				datetimeUTC: false,
				// for datetime axes the 2nd argument is the epoch-ms timestamp
				formatter: (val, timestamp) => formatTime(Number(timestamp ?? val), spansMultipleDays),
			},
			tooltip: { enabled: false },
		},
		tooltip: {
			shared: false,
			intersect: false,
			custom: ({ seriesIndex, dataPointIndex, w }) => {
				const s = w.config.series[seriesIndex];
				const point = s.data[dataPointIndex];
				const color = w.globals.colors[seriesIndex];

				const vesselOnly = String(s.name).replace(/\s*-\s*#.*$/, '');

				const distanceLabel = langdata && langdata.Distance ? langdata.Distance : 'Distance';
				const timeLabel = langdata && langdata.Time ? langdata.Time : 'Time';

				return `
					<div style="min-width:170px;">
						<div style="background:#eceff1;padding:6px 10px;font-weight:400;border-bottom:1px solid #ddd;">
						<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;"></span>
							${vesselOnly}
						</div>
						<div style="padding:6px 10px;line-height:1.7;">
							<div>
								${distanceLabel} (m): <span style="font-weight:600;">${Number(point.y).toFixed(2)} </span>
							</div>
							<div>
								${timeLabel}: <span style="font-weight:600;">${formatTime(Number(point.x), true)} </span>
							</div>
						</div>
					</div>`;
			},
		},
	}), [fiberTimeSeriesColorsKey, markerSizesKey, xAxisMin, xAxisMax, tickAmount, spansMultipleDays]);


	const getDetectionClusterData = (pagination) => {
		let _pagination = { };
		_pagination.total = realtimedetection_data_pagination.total ? realtimedetection_data_pagination.total : 0;
		_pagination.limit = pagination.pageSize ? pagination.pageSize : 10;
		_pagination.skip =  pagination && pagination.current ? parseInt((pagination.current-1) * pagination.pageSize) : 0;

		dispatch({ type : DETECTION_CLUSTER_LIST});


		let pidsinfoId = '';
		let channelId = '';
		let detectionalgorithmtypeId = '';
		// let vesselName = '';
		let intervalMin = '';
		
		if(appliedPidsFilter){
			pidsinfoId = appliedPidsFilter;
		}
		


		let url = `/detectioncluster?pidsinfoId=${parseInt(pidsinfoId)}`;


		// if(regionId){
		// 	body.regionId = regionId;
		// }
		if(appliedChannelsdata){
			channelId = appliedChannelsdata;
			url += `&channelId=${channelId}` ;
		}
		if (appliedvesselFilter && appliedvesselFilter.length > 0) {
			// ALL selected → don't send vessel filter
			if (!appliedvesselFilter.includes(null)) {
				appliedvesselFilter.forEach((vessel) => {
					url += `&id[$in][]=${encodeURIComponent(vessel)}`;
				});
			}
		}
		if(appliedAlgorithm !== null){
			detectionalgorithmtypeId = appliedAlgorithm;
			url += `&detectionalgorithmtypeId=${detectionalgorithmtypeId}`;
		}
		// if(appliedTrajectory!=null){
		// 	body.isTrajectoryCorrected = appliedTrajectory;
		// }
		if(appliedStartDate){
			url += `&starttime[$gte]=${dayjs(appliedStartDate).toISOString()}`;
		}
		if(appliedEndDate){
			url += `&starttime[$lte]=${dayjs(appliedEndDate).toISOString()}`;
		}
		
		if(appliedTimeInterval!=null){
			intervalMin = appliedTimeInterval;
			url += `&intervalMin=${intervalMin}`;
		}
		

		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}${url}`,
			timeout: timeout,
			headers: { 
				'Content-Type': 'application/json', 
				'Authorization': `Bearer ${authtoken}`
			},
		};
      
		axios.request(config)
			.then((response) => {
				if (response.status === 200) {
					realtimedetection_data_pagination.total = response.data.total ? response.data.total : 0;
					realtimedetection_data_pagination.limit = response.data.limit ? response.data.limit : 10;
					realtimedetection_data_pagination.skip = response.data.skip ? response.data.skip : 0;
					realtimedetection_data_pagination.current = pagination.current ? pagination.current : 1;
					dispatch({ type : DETECTION_CLUSTER_LIST_SUCCESS, payload : response.data.data,});
				}else {
					dispatch({ type : DETECTION_CLUSTER_LIST_ERROR});
				}
			})
			.catch((error) => {
				console.log(error);

				dispatch({ type: DETECTION_CLUSTER_LIST_ERROR });

				message.config({
					duration: 5,
					maxCount: 1,
				});

				if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
					message.open({
						type: 'error',
						content: `${langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							: 'It seems the network is unreachable. Please refresh the application.'}`,
					});
				} else {
					message.open({
						type: 'error',
						content: error?.response?.data?.message
							? error.response.data.message
							: 'Something went wrong. Please try again.',
					});
				}
			});
	};
	
	const getDetectionGroupData = (pagination) => {
		let _pagination = { };
		_pagination.total = realtimedetection_data_pagination.total ? realtimedetection_data_pagination.total : 0;
		_pagination.limit = pagination.pageSize ? pagination.pageSize : 10;
		_pagination.skip =  pagination && pagination.current ? parseInt((pagination.current-1) * pagination.pageSize) : 0;

		dispatch({ type : DETECTION_GROUP_LIST});


		let pidsinfoId = '';
		let channelId = '';
		let detectionalgorithmtypeId = '';
		
		if(appliedPidsFilter){
			pidsinfoId = appliedPidsFilter;
		}
		


		let url = `/detectioncollections?pidsinfoId=${parseInt(pidsinfoId)}`;


		// if(regionId){
		// 	body.regionId = regionId;
		// }
		if(appliedChannelsdata){
			channelId = appliedChannelsdata;
			url += `&channelId=${channelId}` ;
		}
		// if (appliedvesselFilter && appliedvesselFilter.length > 0) {
		// 	// ALL selected → don't send vessel filter
		// 	if (!appliedvesselFilter.includes(null)) {
		// 		appliedvesselFilter.forEach((vessel) => {
		// 			url += `&id[$in][]=${encodeURIComponent(vessel)}`;
		// 		});
		// 	}
		// }
		if(appliedAlgorithm !== null){
			detectionalgorithmtypeId = appliedAlgorithm;
			url += `&detectionalgorithmtypeId=${detectionalgorithmtypeId}`;
		}
		// if(appliedTrajectory!=null){
		// 	body.isTrajectoryCorrected = appliedTrajectory;
		// }
		if(appliedStartDate){
			url += `&starttime[$gte]=${dayjs(appliedStartDate).toISOString()}`;
		}
		if(appliedEndDate){
			url += `&starttime[$lte]=${dayjs(appliedEndDate).toISOString()}`;
		}
		if(appliedTrackFilter){
			url += `&trackType=${appliedTrackFilter}`;
		}
		
		// if(appliedTimeInterval!=null){
		// 	intervalMin = appliedTimeInterval;
		// 	url += `&intervalMin=${intervalMin}`;
		// }
		

		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}${url}`,
			timeout: timeout,
			headers: { 
				'Content-Type': 'application/json', 
				'Authorization': `Bearer ${authtoken}`
			},
		};
      
		axios.request(config)
			.then((response) => {
				if (response.status === 200) {
					realtimedetection_data_pagination.total = response.data.total ? response.data.total : 0;
					realtimedetection_data_pagination.limit = response.data.limit ? response.data.limit : 10;
					realtimedetection_data_pagination.skip = response.data.skip ? response.data.skip : 0;
					realtimedetection_data_pagination.current = pagination.current ? pagination.current : 1;
					dispatch({ type : DETECTION_GROUP_LIST_SUCCESS, payload :response.data.data,});
				}else {
					dispatch({ type : DETECTION_GROUP_LIST_ERROR});
				}
			})
			.catch((error) => {
				console.log(error);

				dispatch({ type: DETECTION_GROUP_LIST_ERROR });

				message.config({
					duration: 5,
					maxCount: 1,
				});

				if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
					message.open({
						type: 'error',
						content: `${langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							: 'It seems the network is unreachable. Please refresh the application.'}`,
					});
				} else {
					message.open({
						type: 'error',
						content: error?.response?.data?.message
							? error.response.data.message
							: 'Something went wrong. Please try again.',
					});
				}
			});
	};

	const loadAisData = () => {
		let startTime = '';
		let endTime = '';
		let aisName = '';

		const { start, end } = computeDateRange();

		if (appliedaisNameFilter && appliedaisNameFilter.length > 0) {
			// ALL selected → don't send vessel filter
			if (!appliedaisNameFilter.includes(null)) {
				appliedaisNameFilter.forEach((vessel) => {
					aisName += `&name[$in][]=${encodeURIComponent(vessel)}`;
				});
			}
		}

		if (start) {
			startTime = `&datetime[$gte]=${dayjs(start).toISOString()}`;
		}

		if (end) {
			endTime = `&datetime[$lte]=${dayjs(end).toISOString()}`;
		}

		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}/aisdata?isMapView=true${startTime}${endTime}${aisName}`,
			timeout: timeout,
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
				Authorization: `Bearer ${authtoken}`
			}
		};

		axios
			.request(config)
			.then((response) => {
				if (response.status === 200) {
					dispatch({
						type: AIS_DATA_LIST_SUCCESS,
						payload: response.data
					});
				}
			})
			.catch((error) => {
				dispatch({
					type: AIS_DATA_LIST_ERROR
				});

				if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
					message.config({
						duration: 5,
						maxCount: 1,
					});

					message.open({
						type: 'error',
						content: langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							: 'It seems the network is unreachable. Please refresh the application.',
					});
				} else {
					message.config({
						duration: 5,
						maxCount: 1,
					});

					message.open({
						type: 'error',
						content: `${langdata && langdata.SomethingWentWrong
							? langdata.SomethingWentWrong
							: 'Something Went Wrong!'}`,
					});
				}
			});
	};

	const getDetectionClusterVesselData = () => {

		dispatch({ type: DETECTION_CLUSTER_VESSEL_DATA_LIST });

		let pidsinfoId = '';
		let channelId = '';
		let detectionalgorithmtypeId = '';
		// let vesselName = '';
		let intervalMin = '';

		if (pidsFilter) {
			pidsinfoId = pidsFilter;
		}
		

		// PERFORMANCE: this request only needs the vessel id + name to fill the
		// "Filter by Vessel Name" dropdown, but it runs on every drawer change.
		// $select keeps the response from carrying the huge lat/long/time arrays.
		// let url = `/detectioncluster?pidsinfoId=${parseInt(pidsinfoId)}&$sort[vesselName]=1&$select[]=id&$select[]=vesselName`;
		let url = `/detectioncluster?pidsinfoId=${parseInt(pidsinfoId)}&$sort[vesselName]=1`;

		const { start, end } = computeDateRange();

		if(channelsdata){
			channelId = channelsdata;
			url += `&channelId=${channelId}` ;
		}
		// if(appliedvesselFilter){
		// 	vesselName = appliedvesselFilter;
		// 	url += `&vesselName=${vesselName}` ;
		// }
		if(algorithm !== null){
			detectionalgorithmtypeId = algorithm;
			url += `&detectionalgorithmtypeId=${detectionalgorithmtypeId}`;
		}
		// if(appliedTrajectory!=null){
		// 	body.isTrajectoryCorrected = appliedTrajectory;
		// }
		if(start){
			url += `&starttime[$gte]=${dayjs(start).toISOString()}`;
		}
		if(end){
			url += `&starttime[$lte]=${dayjs(end).toISOString()}`;
		}
		
		if(timeInterval!=null){
			intervalMin = timeInterval;
			url += `&intervalMin=${intervalMin}`;
		}

		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}${url}`,
			timeout: timeout,
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${authtoken}`
			},
		};

		axios.request(config)
			.then((response) => {
				if (response.status === 200) {

					const vesselData = response.data.data.map((item) => ({
						
						id: item.id,
						vesselName: item.vesselName
					}));

					dispatch({
						type: DETECTION_CLUSTER_VESSEL_DATA_LIST_SUCCESS,
						payload: vesselData
					});

				} else {
					dispatch({
						type: DETECTION_CLUSTER_VESSEL_DATA_LIST_ERROR
					});
				}
			})
			.catch((error) => {
				console.log(error);

				dispatch({
					type: DETECTION_CLUSTER_VESSEL_DATA_LIST_ERROR
				});

				message.config({
					duration: 5,
					maxCount: 1,
				});

				if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
					message.open({
						type: 'error',
						content: `${langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
							: 'It seems the network is unreachable. Please refresh the application.'}`,
					});
				} else {
					message.open({
						type: 'error',
						content: error?.response?.data?.message
							? error.response.data.message
							: 'Something went wrong. Please try again.',
					});
				}
			});
	};
	const getAISNameFilter = () => {

		dispatch({ type: AIS_NAME_FILTER });

		// let pidsort = '';
		let startTime = '';
		let endTime = '';
		// let aisName = '';
	
		const { start, end } = computeDateRange();
	
		// if(pidsFilter){
		// 	pidsort = `&pidsinfoId=${pidsFilter}`;
		// }
	
		if(start){
			startTime = `&datetime[$gte]=${dayjs(start).toISOString()}`;
		}
		if(end){
			endTime = `&datetime[$lte]=${dayjs(end).toISOString()}`;
		}
		// if(appliedaisNameFilter.length > 0){
		// 	aisName = `&name=${appliedaisNameFilter}`;
		// }
	
	
		// if(channelsdata){
		// 	params = params + `&channelinfoId=${channelsdata}`;
		// }
		
		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}/aisdata?isMapView=true${startTime}${endTime}`,
			timeout: timeout,
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
				Authorization:`Bearer ${authtoken}`
			}
		}; 

		axios.request(config)
			.then((response) => {
				if (response.status === 200) {

					const aisdata = response.data.data.map((item) => ({
						name: item.name ? item.name : 'No Data',
					}));

					dispatch({
						type: AIS_NAME_FILTER_SUCCESS,
						payload: aisdata
					});

				} else {
					dispatch({
						type: AIS_NAME_FILTER_ERROR
					});
				}
			})
			.catch((error) => {
				console.log(error);

				dispatch({
					type: AIS_NAME_FILTER_ERROR
				});

				message.config({
					duration: 5,
					maxCount: 1,
				});

				if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
					message.open({
						type: 'error',
						content: `${
							langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
								? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication
								: 'It seems the network is unreachable. Please refresh the application.'
						}`,
					});
				} else {
					message.open({
						type: 'error',
						content: error?.response?.data?.message
							? error.response.data.message
							: 'Something went wrong. Please try again.',
					});
				}
			});
	};

	const loadDetectionAlgorithm = ()=>{    
		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${api}/detectionalgorithmtype?pipelineId=${pipelineId}&$sort[id]=1`,
			timeout: timeout,
			headers: { 
				'Content-Type': 'application/json', 
				Authorization:`Bearer ${authtoken}`				
			},
		};
		dispatch({type : DETECTION_ALGORITHM_LIST});
		axios.request(config)
			.then((response) => {
				if(response.status == 200 || response.status == 201){	
											
					dispatch({ type : DETECTION_ALGORITHM_LIST_SUCCESS, payload: response.data.data });
				} else {
					dispatch({ type : DETECTION_ALGORITHM_LIST_ERROR});			
				}
			})
			.catch((error) => {
				console.log(error);
				dispatch({ type : DETECTION_ALGORITHM_LIST_ERROR });
				if(error.code == 'ECONNABORTED' || error.code == 'ERR_NETWORK') {
					message.config({
						duration : 5,
						maxCount : 1,
					});
					message.open({
						type: 'error',
						content: langdata && langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication ? langdata.ItseemsthenetworkisunreachablePleaserefreshtheapplication :  'It seems the network is unreachable. Please refresh the application.',
					});
				} else {
					// Modal.warning({ title: langdata && langdata.Unabletoupdatealgorithm ? langdata.Unabletoupdatealgorithm : 'Unable to update algorithm.' });											
				}
			});
	};

	// const handleSetregion = (e,options) =>{
	// 	localStorage.setItem(`${dashboardtype}_REGION_ID`,e);
	// 	if(admin_roles.includes(permission)) {
	// 		setRegionId(e);
	// 	} else {
	// 		setRegionId(options.region);
	// 		setAllocationId(e);
			
	// 	}	};

	const filterOption = (input, option) =>
		(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

	const infoContent = (
		<Card size="small" bordered="false" type="inner" style={{ width: 300 }}> 
			<p>{langdata && langdata.MyVesselInfoContent ? langdata.MyVesselInfoContent :'This module is used to track vessels after clustering multiple vessel detections into groups.'}</p>
			<br />
		</Card>
	);



	const handleclusterfilter = (value) =>{

		setcluster(value);

		if (value === 2){
			setclusterCheck(true);
		}
		else{
			setclusterCheck(false);
		}



	};
	const handlepidsfilter = (value) =>{
		setchannels(null);
		setvesselFilter(null);
		setpidsFilter(value);
	};
	const handleTrackTypeFilter = (value) => {
		setTrackFilter(value);
	};
	const handlevesselfilter = (values) => {
		
		// ALL selected
		if (values.includes(null)) {
			// If ALL was just selected, keep only ALL
			setvesselFilter([null]);
			return;
		}

		// Normal vessel selection
		setvesselFilter(values);
	};
	const handleAisNamefilter = (values) => {
		
		// ALL selected
		if (values.includes(null)) {
			// If ALL was just selected, keep only ALL
			setAisNameFilter([null]);
			return;
		}

		// Normal vessel selection
		setAisNameFilter(values);
	};

	const handleSetChannel = (e,) =>{
		setvesselFilter(null);
		setchannels(e);
	};
	const handleSetAlgorithm = (e,) =>{
		setvesselFilter(null);
		setalgorithm(e);
	};
	// const handleSetTrajectory = (e,) =>{
	// 	settrajectory(e);
	// };

	const handleFilterDrawer = () => {
		setFilterdrawer(true);
	};

	const handleTimeInterval = (value) => {
		setvesselFilter(null);
		setTimeInterval(value);
	};

	const onCloseFilterDrawer = () => {
		setFilterdrawer(false);
	};


	useEffect(() => {

		getDetectionClusterVesselData();
		// loadAsnData();

	}, [
		pidsFilter,
		// vesselFilter,
		channelsdata,
		algorithm,
		timeInterval,
		filterLastHour,
		advDateRange,
		startTime,
		endTime,timeSelection
	]);
	useEffect(() => {

		getAISNameFilter();
		// getDetectionClusterVesselData();
		// loadAsnData();

	}, [
		// pidsFilter,
		// // vesselFilter,
		// channelsdata,
		// algorithm,
		// timeInterval,
		filterLastHour,
		advDateRange,
		startTime,
		endTime,timeSelection
	]);

	// ---- Apply button handler — commits the drawer's DRAFT filter values
	// (pidsFilter, channelsdata, algorithm, trajectory, and the computed date
	// range) to the "applied" state, which is what the fetch useEffect
	// actually depends on. This is the ONLY place a filter change results in
	// a new API call — exactly mirroring AlarmReport.jsx's
	// handleFilterAlarm. ----
	const handleFilterDetection = () => {
		const { start, end } = computeDateRange();

		setAppliedPidsFilter(pidsFilter);
		setAppliedvesselFilter(vesselFilter);
		setAppliedaisNameFilter(aisNameFilter);
		setAppliedChannelsdata(channelsdata);
		setAppliedTrackFilter(TrackFilter);
		setAppliedAlgorithm(algorithm);
		setAppliedTimeInterval(timeInterval);
		// setAppliedTrajectory(trajectory);
		setAppliedStartDate(start);
		setAppliedEndDate(end);

		setFilterdrawer(false);
	};

	// Fallback map center — first available pipeline point.
	let mapCenter = [];
	if (mapdata && mapdata[0]) {
		mapCenter.push(mapdata[0].latitude, mapdata[0].longitude);
	}

	// ============================================================
	// LIST VIEW — table columns (mirrors RealtimeDetection.jsx's `columns`).
	// Built straight off the fields already present on DetectionClusterData,
	// with the same superadmin-only gating RealtimeDetection.jsx uses for
	// Channel Name / Detection Algorithm.
	// ============================================================
	// const columns = [
	// 	{
	// 		title: `${langdata && langdata.eventType ? langdata.eventType : 'Event Type'}`,
	// 		dataIndex: 'eventType',
	// 		key: 'eventType',
	// 	},
	// 	{
	// 		title: `${langdata && langdata.PidsName ? langdata.PidsName : 'Pids Name'}`,
	// 		dataIndex: 'pidsName',
	// 		key: 'pidsName',
	// 	},

	// 	...(
	// 		['superadmin'].includes(permission)
	// 			? [
	// 				{
	// 					title: `${langdata && langdata.channelName ? langdata.channelName : 'Channel Name'}`,
	// 					dataIndex: 'channelName',
	// 					key: 'channelName',
	// 				}
	// 			]
	// 			: []
	// 	),

	// 	...(
	// 		['superadmin'].includes(permission)
	// 			? [
	// 				{
	// 					title: langdata?.DetectionAlgorithm
	// 						? langdata.DetectionAlgorithm
	// 						: 'Detection Algorithm',
	// 					dataIndex: 'detectionAlgorithmTypeName',
	// 					key: 'detectionAlgorithmTypeName',
	// 				}
	// 			]
	// 			: []
	// 	),

	// 	{
	// 		title: `${langdata && langdata.ModelName ? langdata.ModelName : 'Model Name'}`,
	// 		dataIndex: 'multipleModelName',
	// 		key: 'multipleModelName',
	// 	},

	// 	{
	// 		title: langdata?.startFiberLength
	// 			? langdata.startFiberLength + ' (m)'
	// 			: 'Start Fiber Length (m)',
	// 		dataIndex: 'startFiberLength',
	// 		key: 'startFiberLength',
	// 	},

	// 	{
	// 		title: langdata?.endFiberLength
	// 			? langdata.endFiberLength + ' (m)'
	// 			: 'End Fiber Length (m)',
	// 		dataIndex: 'endFiberLength',
	// 		key: 'endFiberLength',
	// 	},

	// 	{
	// 		title: langdata?.DateTime
	// 			? langdata.DateTime
	// 			: 'Date Time',
	// 		dataIndex: 'starttime',
	// 		key: 'starttime',
	// 	},
	// ];

	useEffect(() => {
		if (
			algorithm === null &&
        realtimedetection_data.detectionalgorithm?.length > 0
		) {
			const firstAlgorithm =
            realtimedetection_data.detectionalgorithm[0].id;

			setalgorithm(firstAlgorithm);
			setAppliedAlgorithm(firstAlgorithm);
		}
	}, [realtimedetection_data.detectionalgorithm, algorithm]);

	return (
		<>
			<Content>
				<Row
					style={{
						margin: '10px',
						paddingLeft: '10px',
						paddingRight: '10px',
						background: '#FFFFFF',
						alignItems:'center',
					}}
				>
					<Col span={4}>
						<Row>
							<Space>
								<FundViewOutlined style={{fontSize:'30px'}}/>
							</Space>
							<Space>&nbsp;&nbsp;</Space>
							<Title level={4} style={{ paddingTop:'10px'}}> {langdata && langdata.MyVessel ? langdata.MyVessel :'My Vessel'}</Title>
						</Row>
					</Col>




					<Col style ={{display:'flex',justifyContent:'flex-end'}} span={20} >
						<Row style ={{display:'flex',justifyContent:'flex-end',alignItems:'center'}}>
							<Col span={24} style={{display:'flex',flexWrap:'wrap',justifyContent:'flex-end', alignItems:'center'}}>

								{/* <Tooltip placement="bottom" title={langdata && langdata.Filterbyregion ? langdata.Filterbyregion :'Filter by region'}>
									<Space>
										<Select
											style={{ minWidth: '200px', textAlign: 'left', margin:'5px'}}
											showSearch
											filterOption={filterOption}
											placeholder={langdata && langdata.SelectRegion ? langdata.SelectRegion :'Select Region'}
											optionFilterProp="children"
											value={ admin_roles.includes(permission) ? regionId : allocationId }
											options={regions}
											onSelect={(e, options)=>handleSetregion(e, options)}
										/>
									</Space>
								</Tooltip> */}
								<Space>
									<Button disabled={filterButton} style ={{margin:'5px',marginLeft:'8px'}} type='primary' onClick={handleFilterDrawer}><FilterOutlined /></Button>
								</Space>

								<Space>
									<Typography>
										<Popover title={langdata && langdata.MyVessel ? langdata.MyVessel :' My Vessel'}  content={infoContent} trigger="click">
											<Avatar size="small" style={{marginTop:'5px',marginBottom:'5px',marginLeft:'8px',cursor:'pointer'}}><InfoOutlined /></Avatar>
										</Popover>
									</Typography>
								</Space>
							</Col>
						</Row>
					</Col>
				</Row>

				<Row style={{
					margin: '10px',
					padding:'10px',
					paddingBottom:'2px',
					background: '#FFFFFF',
					alignItems:'center',
				}}>
					<Col span={12}>
						<Radio.Group
							style={{ margin: '0 0 10px 0' }}
							value={view}
							onChange={(e) => setView(e.target.value)}
						>
							{/* <Radio.Button value="List">
								{langdata && langdata.List ? langdata.List : 'List'}
							</Radio.Button> */}
							<Radio.Button value="Map">
								{langdata && langdata.Map ? langdata.Map : 'Map'}
							</Radio.Button>
							<Radio.Button value="Graph">
								{langdata && langdata.Graph ? langdata.Graph : 'Graph'}
							</Radio.Button>
						</Radio.Group>
					</Col>
				</Row>

				{ view === 'Map' ? (
					<Row span={24} style={{
						margin: '10px',
						paddingLeft: '10px',
						paddingRight: '10px',
						background: '#FFFFFF',
						alignItems:'center'
					}} >
						<Col span={24}>
							{map_type && map_type !== 'leaflet' ? (
								<GoogleMap height={'70vh'} showAISVessels={true} />
							) : (
								<div className="App" style={{ textAlign: 'left', margin: '10px', height: '70vh' }}>
									<SeaMap
										center={mapCenter.length > 0 ? mapCenter : undefined}
										zoom={14}
										style={{ height: '100%' }}
										mapContainerProps={{ scrollWheelZoom: true }}
										pipelineTracks={pipelineTracks}
										fiberTracks={fiberTracks}
										pipelineBorderTracks={pipelineBorderTracks}
										asnData={stableASNdata}
										optionalPathTracks={optionalPathTracks}
										kmpMarkers={KMP}
										wbMarkers={WB}
										tpMarkers={Turn}
										tlpMarkers={TLP}
										chMarkers={CH}
										detectionTrajectories={detectionTrajectoriesForMap}
										showAISVessels={true}
									/>
								</div>
							)}
						</Col>
					</Row>
				) : (
					// ---- Graph view: only the fiberDistance-vs-timeStart chart. ----
					<Row span={24} style={{
						margin: '10px',
						paddingLeft: '10px',
						paddingRight: '10px',
						background: '#FFFFFF',
						alignItems:'center'
					}} >
						<Col span={24}>
							<div className="App" style={{ textAlign: 'left', margin: '10px' }}>
								<div ref={ref}>
									<Card className="card-Shadow-dashboard">
										{ detectionFiberTimeSeries.length > 0 ? (
										// Outer div scrolls horizontally; inner div is as wide as the chart.
											<div style={{ overflowX: 'auto', overflowY: 'hidden', width: '100%' }}>
												<div style={{ width: chartWidth }}>
													<Chart
														height={500}
														width={chartWidth}
														options={fiberDistanceTimeOptions}
														series={detectionFiberTimeSeries}
														type="line"
													/>
												</div>
											</div>
										) : (
											<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
										)}
									</Card>
								</div>
							</div>
						</Col>
					</Row>
				)}

				<Drawer
					title={langdata && langdata.Filteroptions ? langdata.Filteroptions : 'Filter options'}
					onClose={onCloseFilterDrawer}
					open={filterDrawer}
				>
					<Form layout="vertical">
						{/* <Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.FilterbyTrajectoryMethod ? langdata.FilterbyTrajectoryMethod : 'Filter by Trajectory Method'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectTrajectoryMethod ? langdata.SelectTrajectoryMethod : 'Select Trajectory Method'}
										optionFilterProp="children"
										value={trajectory}
										options={trajectoryOptions}
										onSelect={(e) => handleSetTrajectory(e)}
									/>
								</Form.Item>
							</Col>
						</Row> */}

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.FilterbyCluster ? langdata.FilterbyCluster : 'Filter by Cluster'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectCluster ? langdata.SelectCluster : 'Select Cluster'}
										optionFilterProp="children"
										value={cluster}
										options={clusterfilter}
										onSelect={(e) => handleclusterfilter(e)}
									/>
								</Form.Item>
							</Col>
						</Row>
						{clusterCheck ?  <Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.FilterbytrackType ? langdata.FilterbytrackType : ' Filter By Track Type'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.trackType ? langdata.trackType : 'Track Type'}
										optionFilterProp="children"
										value={TrackFilter}
										options={tracktypefilter}
										onSelect={handleTrackTypeFilter}
									/>
								</Form.Item>
							</Col>
						</Row> :null}
						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.Filterbypids ? langdata.Filterbypids : 'Filter by PIDS'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectPIDS ? langdata.SelectPIDS : 'Select PIDS'}
										optionFilterProp="children"
										value={pidsFilter}
										options={pidsfilter}
										onSelect={handlepidsfilter}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.Filterbychannel ? langdata.Filterbychannel : 'Filter by Channel'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectChannel ? langdata.SelectChannel : 'Select Channel'}
										optionFilterProp="children"
										value={channelsdata}
										options={channels}
										disabled={pidsFilter == null}
										onSelect={(e) => handleSetChannel(e)}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.FilterbyAlgorithm ? langdata.FilterbyAlgorithm : 'Filter by Algorithm'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={
											langdata && langdata.SelectAlgorithm
												? langdata.SelectAlgorithm
												: 'Select Algorithm'
										}
										optionFilterProp="children"
										value={algorithm}
										options={algorithamOptions}
										onSelect={(e) => handleSetAlgorithm(e)}
										// onChange={(e)=>handleSetAlgorithm(e)}
									/>
								</Form.Item>
							</Col>
						</Row>


						{clusterCheck ? null :<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.Groupby ? langdata.Groupby : 'Group By'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectTime ? langdata.SelectTime : 'Select Time'}
										optionFilterProp="children"
										value={timeInterval}
										options={timeIntervalOptions}
										onSelect={handleTimeInterval}
										disabled={clusterCheck}
									/>
								</Form.Item>
							</Col>
						</Row>}

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label={langdata && langdata.DetectionsinLast ? langdata.DetectionsinLast : 'Detections in Last'}>
									<Select
										showSearch
										filterOption={filterOption}
										placeholder={langdata && langdata.SelectTime ? langdata.SelectTime : 'Select Time'}
										optionFilterProp="children"
										value={filterLastHour}
										options={lastHours}
										onSelect={handleLastHour}
										disabled={timeSelection}
									/>
								</Form.Item>
							</Col>
						</Row>

						{ timeSelection === true ? null :
							<> 
								{clusterCheck ? null : <Row gutter={24}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.FilterbyVesselName ? langdata.FilterbyVesselName : 'Filter by Vessel Name'}>
											<Select
												showSearch
												mode="multiple"
												filterOption={filterOption}
												placeholder={langdata && langdata.SelectVessel ? langdata.SelectVessel : 'Select Vessel Name'}
												optionFilterProp="children"
												value={vesselFilter}
												options={vesselOptions}
												onChange={handlevesselfilter}
												disabled={clusterCheck} />
										</Form.Item>
									</Col>
								</Row>}
								<Row gutter={24}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.FilterbyAISName ? langdata.FilterbyAISName : 'Filter by AIS Name'}>
											<Select
												showSearch
												mode="multiple"
												filterOption={filterOption}
												placeholder={langdata && langdata.SelectAISName ? langdata.SelectAISName : 'Select AIS Name'}
												optionFilterProp="children"
												value={aisNameFilter}
												options={aisNameOptions}
												onChange={handleAisNamefilter} />
										</Form.Item>
									</Col>
								</Row>
							</>
						}					

						<Row gutter={24}>
							<Col span={24}>
								<Checkbox onChange={changeTimeSelection} checked={timeSelection}>{langdata && langdata.Advancedoptions ? langdata.Advancedoptions : 'Advanced options'}</Checkbox>
							</Col>
						</Row>

						{timeSelection === true ? (
							<>
								<Row gutter={24} style={{ marginTop: '20px' }}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.Date ? langdata.Date : 'Date'}>
											<RangePicker
												onChange={handleAdvDateRange}
												style={{ minWidth: '100%' }}
												disabledDate={disabledDate}
												allowClear={false}
												format={`${settingsdateformat}`}
												value={advDateRange}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={24}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.StartTime ? langdata.StartTime : 'Start Time'}>
											<TimePicker allowClear={false} onChange={handleStartTime} style={{ minWidth: '100%' }} value={startTime} />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={24}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.EndTime ? langdata.EndTime : 'End Time'}>
											<TimePicker allowClear={false} onChange={handleEndTime} style={{ minWidth: '100%' }} value={endTime} />
										</Form.Item>
									</Col>
								</Row>
								{clusterCheck ? null :
									<Row gutter={24}>
										<Col span={24}>
											<Form.Item label={langdata && langdata.FilterbyVesselName ? langdata.FilterbyVesselName : 'Filter by Vessel Name'}>
												<Select
													showSearch
													mode="multiple"
													filterOption={filterOption}
													placeholder={langdata && langdata.SelectVessel ? langdata.SelectVessel : 'Select Vessel Name'}
													optionFilterProp="children"
													value={vesselFilter}
													options={vesselOptions}
													onChange={handlevesselfilter} />
											</Form.Item>
										</Col>
									</Row>}
								<Row gutter={24}>
									<Col span={24}>
										<Form.Item label={langdata && langdata.FilterbyAISName ? langdata.FilterbyAISName : 'Filter by AIS Name'}>
											<Select
												showSearch
												mode="multiple"
												filterOption={filterOption}
												placeholder={langdata && langdata.SelectAISName ? langdata.SelectAISName : 'Select AIS Name'}
												optionFilterProp="children"
												value={aisNameFilter}
												options={aisNameOptions}
												onChange={handleAisNamefilter} />
										</Form.Item>
									</Col>
								</Row>
							</>
						) : null}

						<Row gutter={24}>
							<Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
								<Button type='primary' onClick={handleFilterDetection}>{langdata && langdata.Apply ? langdata.Apply : 'Apply'}</Button>
							</Col>
							<Col span={12} style={{ display: 'flex', marginTop: '10px' }}>
								<Button onClick={onCloseFilterDrawer}>{langdata && langdata.Close ? langdata.Close : 'Close'}</Button>
							</Col>
						</Row>
					</Form>
				</Drawer>

			</Content>

			<Spin spinning={realtimedetection_data.pending} fullscreen />

		</>
	);
}